<script setup>
import { ref, watch, computed } from 'vue'
import { Head, router, usePage } from '@inertiajs/vue3'
import { PhBook } from "@phosphor-icons/vue"
import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Modal from '@pages/staff/components/SubjectModal.vue'
import Table from '@pages/Shared/ui/Table.vue'
import Prompt from '@pages/Shared/Prompt.vue'
import { flashToast } from "@/toast.js"
import { capitalize } from '@/helpers.js'

const props = defineProps({
    subjects: Object,
    sections: Array,
    filters:  Object,
})

const openModal        = ref(false)
const modalStatus      = ref(null)
const modalTitle       = ref('')
const showPrompt       = ref(false)
const selectedSubject  = ref(null)
const deleteId         = ref(null)

const filters = ref({ search: null, section: null, class: null, ...props.filters })

const page = usePage()
const can  = computed(() => page.props.can || {})

const columns = [
    { key: 'index',    label: '#',                 type: 'index'   },
    { key: 'name',     label: 'Name'                               },
    { key: 'arabic',   label: 'Arabic Name'                        },
    { key: 'sections', label: 'Sections Offering', type: 'slot'    },
    { key: 'classes',  label: 'Classes Offering',  type: 'slot'    },
    { key: 'actions',  label: '',                  type: 'actions' },
]

const filterClassOptions = computed(() => {
    if (!filters.value.section) return []
    const section = (props.sections ?? []).find(s => s.id === filters.value.section)
    return section?.classes?.map(c => ({ label: c.name, value: c.id })) ?? []
})

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    )
    router.get('/staff/manage-subjects', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch  = (value) => { filters.value.search = value; applyFilters() }

const handleFilter  = (newFilters) => {
    const sectionChanged = Object.prototype.hasOwnProperty.call(newFilters, 'section') &&
                           newFilters.section !== filters.value.section
    filters.value = { ...filters.value, ...newFilters }
    if (sectionChanged) filters.value.class = null
    applyFilters()
}

const openAddSubjectModal = () => {
    selectedSubject.value = null
    modalTitle.value      = 'Add Subject'
    modalStatus.value     = 'create'
    openModal.value       = true
}

const editSubject = (id) => {
    selectedSubject.value = props.subjects.data.find(s => s.id === id) ?? null
    modalTitle.value      = 'Edit Subject'
    modalStatus.value     = 'edit'
    openModal.value       = true
}

const viewSubject = (id) => {
    selectedSubject.value = props.subjects.data.find(s => s.id === id) ?? null
    modalTitle.value      = 'View Subject'
    modalStatus.value     = 'view'
    openModal.value       = true
}

const deleteSubject = (id) => { deleteId.value = id; showPrompt.value = true }

const confirmDelete = () => {
    router.delete(`/staff/manage-subjects/${deleteId.value}`, {
        onSuccess: (page) => { showPrompt.value = false; flashToast(page.props.flash) }
    })
}

const handleSubmit = (form) => {
    const options = {
        onSuccess: (page) => { openModal.value = false; flashToast(page.props.flash) }
    }
    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/staff/manage-subjects/${form.id}`, options)
    } else {
        form.post('/staff/manage-subjects', options)
    }
}
</script>

<template>
    <Head title="Subjects Management" />
    <Layout>

        <Prompt
            title="subject"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            :status="modalStatus"
            :subject="selectedSubject"
            :sections="sections"
            @submitted="handleSubmit"
        />

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Subjects</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all subjects</p>
            </div>
            <button
                v-if="can.createSubject"
                @click="openAddSubjectModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhBook :size="20" />
                Add Subject
            </button>
        </div>

        <Table
            :data="subjects"
            :columns="columns"
            variant="staff"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'section', label: 'Section',
                    options: [
                        { label: 'All Sections', value: null },
                        ...(sections ?? []).map(s => ({ label: s.name, value: s.id })),
                    ],
                    value: filters.section,
                },
                {
                    key: 'class', label: 'Class',
                    options: [
                        { label: 'All Classes', value: null },
                        ...filterClassOptions,
                    ],
                    value: filters.class,
                    disabled: !filters.section,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @edit="editSubject"
            @view="viewSubject"
            @delete="deleteSubject"
        >
            <template #sections="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.sections?.length">
                        <span
                            v-for="section in row.sections" :key="section.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                   bg-primary-50 text-primary-700 rounded-md border border-primary-100"
                        >
                            {{ capitalize(section.name) }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>

            <template #classes="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.classes?.length">
                        <span
                            v-for="cls in row.classes" :key="cls.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                   bg-purple-50 text-purple-700 rounded-md border border-purple-100"
                        >
                            {{ capitalize(cls.name) }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>
        </Table>

    </Layout>
</template>